import Countdown from '@liveauctioneers/caterwaul-components/lib/Countdown/Countdown';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useFeatureFlags } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { loadPageData } from '@/redux/modules/appState';
import {
    getCatalogIsInstantAutopay,
    getCatalogIsTimedPlusBiddingOpen,
    getCatalogIsTimedPlusClosing,
    getCatalogStartTime,
    getCatalogTimedPlusBiddingEndsFromTs,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getIsRegistered } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics/registration';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';

import CatalogRegistrationMessages from './CatalogRegistrationMessages';
import CatalogStatusBar from './CatalogStatusBar';

export type TimedPlusAuctionStatusProps = {
    catalogId: number;
    className?: string;
    setForceIsLive: (force: boolean) => void;
};

const TimedPlusAuctionStatus = ({ catalogId, className, setForceIsLive }: TimedPlusAuctionStatusProps) => {
    const { variants } = useAmplitudeExperiments();
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const featureFlags = useFeatureFlags();
    const reloadPageData = useCallback(() => {
        dispatch(loadPageData({ amplitudeExperimentFlags: variants, featureFlags, location }));
    }, [dispatch, location, variants, featureFlags]);
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const biddingEndsFrom = useAppSelector((state) => getCatalogTimedPlusBiddingEndsFromTs(state, catalogId));
    const biddingOpens = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    const timedPlusBiddingOpen = useAppSelector((state) => getCatalogIsTimedPlusBiddingOpen(state, catalogId));
    const lotsAreClosing = useAppSelector((state) => getCatalogIsTimedPlusClosing(state, catalogId));
    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const registerForCatalog = () => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                modalContext,
            })
        );
    };

    return (
        <StyledTimedPlusAuctionStatus
            className={className}
            data-testid="timed-plus-auction-status"
            suppressHydrationWarning
        >
            {registered && <CatalogRegistrationMessages catalogId={catalogId} />}
            <StyledAuctionType>
                <FormattedMessage
                    id="catalogPage.catalogStatus.timedPlusAuction"
                    values={{ numberOfAuctions: 1 }}
                />
            </StyledAuctionType>
            <CountdownHeader>
                <FormattedMessage
                    id={
                        lotsAreClosing
                            ? 'catalogPage.catalogStatus.inProgress'
                            : timedPlusBiddingOpen
                              ? 'catalogPage.catalogStatus.timedPlusBiddingEnds'
                              : 'catalogPage.catalogStatus.timedPlusStarts'
                    }
                />
            </CountdownHeader>
            {lotsAreClosing ? (
                <CatalogStatusBar catalogId={catalogId} />
            ) : (
                <StyledCountDown
                    daysText="days"
                    extendedCount
                    hoursText="hrs"
                    leadingZeroes={false}
                    minutesText="mins"
                    onCountdownEnd={() => {
                        setForceIsLive(true);
                        reloadPageData();
                    }}
                    secondsText="secs"
                    showSeconds
                    timeStamp={timedPlusBiddingOpen ? biddingEndsFrom : biddingOpens}
                />
            )}
            {!registered && (
                <StyledRegisterButton
                    data-testid="liveCatalogRegisterButton"
                    onClick={registerForCatalog}
                    style={isWhiteLabel ? 'primary' : 'critical'}
                    variant="filled"
                >
                    {formatMessage({
                        id: 'catalogPage.catalogStatus.registerToBid',
                    })}
                </StyledRegisterButton>
            )}
            <Link
                rel="noopener"
                target="_blank"
                to="https://support.liveauctioneers.com/hc/en-gb/articles/24574071591313-How-do-Timed-auctions-work"
            >
                <FormattedMessage id="catalogPage.catalogStatus.aboutTimedPlus" />
            </Link>
        </StyledTimedPlusAuctionStatus>
    );
};

export default TimedPlusAuctionStatus;

const StyledTimedPlusAuctionStatus = styled.div`
    height: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
`;

const StyledAuctionType = styled(LabelStrong)`
    margin-bottom: 8px;
`;

const CountdownHeader = styled.div`
    color: ${({ theme }) => theme.colors.grey100};
    letter-spacing: 1px;
    text-transform: uppercase;
`;

const StyledCountDown = styled(Countdown).attrs(() => ({
    textClassName: 'countdownText',
}))`
    justify-content: center;
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    margin: 16px 0 32px;
    color: ${({ theme }) => theme.colors.black};
    letter-spacing: 0.5px;
    max-width: 230px;

    & > div {
        padding: 0 1px;
        font-size: 32px;
    }

    .countdownText {
        margin-top: 4px;
    }
`;

const StyledRegisterButton = styled(Button)`
    margin-bottom: 16px;
    width: 100%;
`;
